/**
 * Servicio de MessageChannel para comunicación con PinPad Web v2.6.0
 * 
 * Implementa:
 * - Creación de canal bidireccional con iframe
 * - Envío de mensajes con timeout
 * - Manejo de respuestas asíncronas
 * - Cola de mensajes pendientes
 * - Integración con sistema de retry
 * 
 * @module pinpad-v2/message-channel-service
 */

import { logInfo, logError, logWarn, logOperationStart } from '../../utilities/pinpad-v2/logger'
import { parseError } from '../../config/pinpad-v2/error-codes'
import iframeService from './iframe.service'

class MessageChannelService {
  constructor() {
    this.channel = null
    this.port = null
    this.isInitialized = false
    this.pendingMessages = new Map() // messageId -> { resolve, reject, timeoutId, operation }
    this.messageIdCounter = 0
  }

  /**
   * Inicializa el MessageChannel con el iframe
   * @returns {Promise<void>}
   */
  async initialize() {
    if (this.isInitialized) {
      logInfo('MessageChannel already initialized')
      return
    }

    logInfo('Initializing MessageChannel')

    try {
      // Crear el canal de comunicación
      this.channel = new MessageChannel()
      this.port = this.channel.port1

      logInfo('MessageChannel created', {
        port1: !!this.port,
        port2: !!this.channel.port2
      })

      // Configurar listener de mensajes del iframe
      this.port.onmessage = this.handleMessage.bind(this)
      
      // Listener para errores del puerto
      this.port.onmessageerror = (error) => {
        logError('MessageChannel port error', error)
      }

      // Esperar a que el iframe esté listo
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Iframe not ready after 30s'))
        }, 30000)

        iframeService.onReady(() => {
          clearTimeout(timeout)
          resolve()
        })
      })

      const contentWindow = iframeService.getContentWindow()
      
      logInfo('Sending init message to iframe with port2')
      
      // Enviar port2 al iframe para establecer comunicación
      // Según la guía: contentWindow.postMessage('message', '*', [channel.port2])
      contentWindow.postMessage('message', '*', [this.channel.port2])
      
      // Esperar pequeño delay para que el iframe configure su lado
      await new Promise(resolve => setTimeout(resolve, 500))
      
      this.isInitialized = true
      logInfo('MessageChannel initialized successfully')
      
    } catch (error) {
      logError('Failed to initialize MessageChannel', error)
      this.cleanup()
      throw error
    }
  }

  /**
   * Maneja mensajes entrantes del iframe
   * @param {MessageEvent} event
   * @private
   */
  handleMessage(event) {
    const response = this.normalizeIncomingResponse(event?.data)

    if (!response || typeof response !== 'object') {
      logWarn('Received invalid message format', { data: event?.data })
      return
    }

    logInfo('Received message from PinPad Web', { 
      type: response.type,
      messageId: response.messageId,
      hasError: response.RESPUESTA === 'error'
    })

    // Buscar el mensaje pendiente correspondiente
    const pending = this.getPendingMessageForResponse(response)
    
    if (!pending) {
      logWarn('Received response for unknown message', { 
        messageId: response.messageId,
        type: response.type,
        pendingCount: this.pendingMessages.size
      })
      return
    }

    // Limpiar timeout y eliminar de pendientes
    clearTimeout(pending.timeoutId)
    this.pendingMessages.delete(pending.messageId)

    // Verificar si es error
    if (response.RESPUESTA === 'error' || response.ERROR) {
      const error = parseError(response)
      logError(`Operation ${pending.operation} failed`, {
        code: error.code,
        message: error.message,
        response
      }, pending.timerId)
      
      pending.reject(error)
    } else {
      logInfo(`Operation ${pending.operation} succeeded`, {
        type: response.type,
        messageId: response.messageId
      }, pending.timerId)
      
      pending.resolve(response)
    }
  }

  normalizeIncomingResponse(rawData) {
    if (!rawData) {
      return null
    }

    if (typeof rawData === 'string') {
      try {
        return JSON.parse(rawData)
      } catch (_error) {
        return null
      }
    }

    if (typeof rawData === 'object') {
      if (typeof rawData.data === 'string') {
        try {
          return JSON.parse(rawData.data)
        } catch (_error) {
          return rawData
        }
      }

      if (rawData.data && typeof rawData.data === 'object') {
        return rawData.data
      }

      return rawData
    }

    return null
  }

  getPendingMessageForResponse(response) {
    if (response?.messageId && this.pendingMessages.has(response.messageId)) {
      return this.pendingMessages.get(response.messageId)
    }

    if (this.pendingMessages.size === 1) {
      const [pending] = this.pendingMessages.values()
      logWarn('Response without messageId matched to single pending operation', {
        operation: pending.operation,
        pendingCount: this.pendingMessages.size
      })
      return pending
    }

    if (this.pendingMessages.size > 1) {
      const [pending] = this.pendingMessages.values()
      logWarn('Response without messageId matched to oldest pending operation', {
        operation: pending.operation,
        pendingCount: this.pendingMessages.size
      })
      return pending
    }

    return null
  }

  /**
   * Envía un mensaje al iframe con timeout
   * @param {string} type - Tipo de operación (login, readCard, sndVentaDirectaEMV, etc)
   * @param {object} payload - Datos de la operación
   * @param {number} timeout - Timeout en ms (default: 60000)
   * @returns {Promise<object>}
   */
  sendMessage(type, payload = {}, timeout = 60000) {
    if (!this.isInitialized) {
      const error = new Error('MessageChannel not initialized. Call initialize() first.')
      logError('Attempt to send message before initialization', { type, payload })
      return Promise.reject(error)
    }

    if (!this.port) {
      const error = new Error('MessageChannel port not available')
      logError('Port not available', { type })
      return Promise.reject(error)
    }

    return new Promise((resolve, reject) => {
      const messageId = `${type}_${++this.messageIdCounter}_${Date.now()}`
      
      // Iniciar timer para logging
      const timerId = logOperationStart(type, { 
        messageId, 
        payloadKeys: Object.keys(payload),
        timeout 
      })

      const message = {
        type,
        payload,
        messageId
      }

      logInfo('Sending message to PinPad Web', {
        type,
        messageId,
        payloadSize: JSON.stringify(payload).length
      })

      // Configurar timeout
      const timeoutId = setTimeout(() => {
        this.pendingMessages.delete(messageId)
        
        const error = new Error(`Operation ${type} timed out after ${timeout}ms`)
        error.code = 'TIMEOUT'
        error.operation = type
        
        logError('Operation timeout', {
          operation: type,
          messageId,
          timeout
        }, timerId)
        
        reject(error)
      }, timeout)

      // Guardar mensaje pendiente
      this.pendingMessages.set(messageId, {
        operation: type,
        messageId,
        timerId,
        timeoutId,
        resolve: (response) => {
          logInfo(`Operation ${type} completed successfully`, {
            messageId,
            duration: Date.now() - parseInt(messageId.split('_')[2])
          }, timerId)
          resolve(response)
        },
        reject: (error) => {
          logError(`Operation ${type} failed`, {
            messageId,
            error: error.message,
            code: error.code
          }, timerId)
          reject(error)
        }
      })

      logInfo('Message posted to port', {
        message,
        port: this.port,
      })

      // Enviar mensaje al iframe vía port
      try {
        const outboundPayload = {
          ...payload,
          ...(payload?.name ? {} : { name: type })
        }

        this.port.postMessage(outboundPayload)

        logInfo('Message sent successfully', {
          type,
          messageId,
          pendingCount: this.pendingMessages.size
        })
      } catch (error) {
        clearTimeout(timeoutId)
        this.pendingMessages.delete(messageId)
        
        logError('Failed to send message via port', {
          type,
          messageId,
          error: error.message
        }, timerId)
        
        reject(error)
      }
    })
  }

  /**
   * Cierra el canal y limpia recursos
   */
  close() {
    if (!this.isInitialized) {
      logInfo('MessageChannel already closed')
      return
    }

    logInfo('Closing MessageChannel', {
      pendingMessages: this.pendingMessages.size
    })

    // Rechazar todos los mensajes pendientes
    this.pendingMessages.forEach((pending, messageId) => {
      clearTimeout(pending.timeoutId)
      
      const error = new Error('MessageChannel closed while operation in progress')
      error.code = 'CHANNEL_CLOSED'
      error.operation = pending.operation
      
      pending.reject(error)
    })
    
    this.cleanup()
    
    logInfo('MessageChannel closed')
  }

  /**
   * Limpia recursos internos
   * @private
   */
  cleanup() {
    if (this.port) {
      try {
        this.port.close()
      } catch (error) {
        logWarn('Error closing port', error)
      }
      this.port = null
    }
    
    this.pendingMessages.clear()
    this.channel = null
    this.isInitialized = false
    this.messageIdCounter = 0
  }

  /**
   * Verifica si el canal está listo
   * @returns {boolean}
   */
  isReady() {
    return this.isInitialized && this.port !== null
  }

  /**
   * Obtiene el número de mensajes pendientes
   * @returns {number}
   */
  getPendingCount() {
    return this.pendingMessages.size
  }

  /**
   * Reinicia el MessageChannel (cierra y reinicializa)
   * @returns {Promise<void>}
   */
  async restart() {
    logInfo('Restarting MessageChannel')
    this.close()
    await this.initialize()
  }
}

// Singleton
const messageChannelService = new MessageChannelService()

export default messageChannelService
